<template>
  <div v-if="showOnboarding" class="onboarding">
    <div class="onboarding__backdrop" @click="canSkip ? skipOnboarding() : undefined" />
    <UiCard class="onboarding__card">
      <div class="onboarding__header">
        <div class="onboarding__title-wrap">
          <h2>Добро пожаловать в ЛабАктив!</h2>
          <div class="onboarding__step-counter">
            {{ currentStepIndex + 1 }} / {{ onboardingSteps.length }}
          </div>
        </div>
        <UiButton v-if="canSkip" variant="secondary" @click="skipOnboarding">Пропустить</UiButton>
      </div>

      <UiProgress variant="linear" :value="progress" :indeterminate="false" />

      <div class="onboarding__body">
        <section v-if="currentStepIndex === 0" class="onboarding__step">
          <h3>Современная B2B платформа для торговли лабораторным оборудованием</h3>
          <div class="onboarding__features">
            <article>
              <div class="onboarding__icon">🔬</div>
              <h4>1000+ товаров</h4>
              <p>Широкий ассортимент лабораторного оборудования</p>
            </article>
            <article>
              <div class="onboarding__icon">⚖️</div>
              <h4>Тендеры</h4>
              <p>Участвуйте в тендерах и закупках</p>
            </article>
            <article>
              <div class="onboarding__icon">🚚</div>
              <h4>Быстрая доставка</h4>
              <p>Оперативная доставка по всей России</p>
            </article>
          </div>
        </section>

        <section v-else-if="currentStepIndex === 1" class="onboarding__step">
          <h3>Расскажите о своей компании</h3>
          <p class="onboarding__muted">
            Эта информация поможет предложить более релевантные товары и услуги
          </p>

          <div class="onboarding__form-grid">
            <div>
              <label>Тип организации</label>
              <UiSelect v-model="userProfile.companyType" :options="companyTypes" />
            </div>
            <div>
              <label>Отрасль деятельности</label>
              <UiSelect v-model="userProfile.industry" :options="industries" />
            </div>
            <div>
              <label>Размер компании</label>
              <UiSelect v-model="userProfile.companySize" :options="companySizes" />
            </div>
          </div>

          <div class="onboarding__interest-block">
            <label>Интересующие категории товаров</label>
            <div class="onboarding__chips">
              <button
                v-for="category in productCategories"
                :key="category.value"
                type="button"
                :class="[
                  'onboarding__chip',
                  { 'is-active': userProfile.interests.includes(category.value) },
                ]"
                @click="toggleInterest(category.value)"
              >
                {{ category.label }}
              </button>
            </div>
          </div>
        </section>

        <section v-else-if="currentStepIndex === 2" class="onboarding__step">
          <h3>Настройте уведомления</h3>
          <p class="onboarding__muted">Выберите, о чем вы хотите получать уведомления</p>

          <div class="onboarding__notification-list">
            <div
              v-for="setting in notificationSettings"
              :key="setting.key"
              class="onboarding__notification-item"
            >
              <div>
                <div class="onboarding__notification-title">{{ setting.title }}</div>
                <div class="onboarding__muted">{{ setting.description }}</div>
              </div>
              <UiSwitch v-model="userProfile.notifications[setting.key]" />
            </div>
          </div>
        </section>

        <section v-else class="onboarding__step">
          <h3>Все готово!</h3>
          <p class="onboarding__muted">
            Теперь вы можете в полной мере использовать возможности ЛабАктив
          </p>

          <div class="onboarding__next-steps">
            <button
              v-for="step in nextSteps"
              :key="step.title"
              type="button"
              class="onboarding__next-step"
              @click="step.action"
            >
              <div class="onboarding__next-step-title">{{ step.title }}</div>
              <div class="onboarding__muted">{{ step.description }}</div>
            </button>
          </div>
        </section>
      </div>

      <div class="onboarding__actions">
        <UiButton v-if="currentStepIndex > 0" variant="secondary" @click="prevStep">Назад</UiButton>
        <div class="onboarding__spacer" />
        <UiButton :disabled="!canProceed" @click="nextStep">
          {{ currentStepIndex === onboardingSteps.length - 1 ? 'Начать работу' : 'Далее' }}
        </UiButton>
      </div>
    </UiCard>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import UiButton from '@/shared/ui/UiButton.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiProgress from '@/shared/ui/UiProgress.vue'
import UiSelect from '@/shared/ui/UiSelect.vue'
import UiSwitch from '@/shared/ui/UiSwitch.vue'
import { useTutorial } from '@/modules/tutorial/public'
import { useFeatureFlags } from '@/modules/common/public'

const router = useRouter()
const { startTutorial } = useTutorial()
const feature_flags = useFeatureFlags()

const showOnboarding = ref(false)
const currentStepIndex = ref(0)

const onboardingSteps = ['welcome', 'profile', 'notifications', 'completion']

type NotificationKey = 'newProducts' | 'tenders' | 'orders' | 'promotions' | 'newsletter'

interface NotificationSetting {
  key: NotificationKey
  title: string
  description: string
}

const userProfile = ref({
  companyType: '',
  industry: '',
  companySize: '',
  interests: [] as string[],
  notifications: {
    newProducts: true,
    tenders: true,
    orders: true,
    promotions: false,
    newsletter: true,
  } as Record<NotificationKey, boolean>,
})

const companyTypes = [
  { label: 'Выберите тип', value: '' },
  { label: 'ООО', value: 'llc' },
  { label: 'АО', value: 'jsc' },
  { label: 'ИП', value: 'ie' },
  { label: 'Государственное учреждение', value: 'gov' },
  { label: 'НИИ/Университет', value: 'research' },
]

const industries = [
  { label: 'Выберите отрасль', value: '' },
  { label: 'Медицина и здравоохранение', value: 'healthcare' },
  { label: 'Научные исследования', value: 'research' },
  { label: 'Образование', value: 'education' },
  { label: 'Фармацевтика', value: 'pharma' },
  { label: 'Пищевая промышленность', value: 'food' },
  { label: 'Химическая промышленность', value: 'chemical' },
  { label: 'Экология', value: 'ecology' },
]

const companySizes = [
  { label: 'Выберите размер', value: '' },
  { label: 'Малый бизнес (до 50 сотрудников)', value: 'small' },
  { label: 'Средний бизнес (50-250 сотрудников)', value: 'medium' },
  { label: 'Крупный бизнес (250+ сотрудников)', value: 'large' },
]

const productCategories = [
  { label: 'Микроскопы', value: 'microscopes' },
  { label: 'Центрифуги', value: 'centrifuges' },
  { label: 'Спектрометры', value: 'spectrometers' },
  { label: 'Хроматографы', value: 'chromatographs' },
  { label: 'Весы и дозаторы', value: 'scales' },
  { label: 'Термостаты и печи', value: 'heating' },
]

const notificationSettings: NotificationSetting[] = [
  {
    key: 'newProducts',
    title: 'Новые товары',
    description: 'Уведомления о новинках в интересующих категориях',
  },
  {
    key: 'tenders',
    title: 'Тендеры и закупки',
    description: 'Информация о новых тендерах и закупках',
  },
  {
    key: 'orders',
    title: 'Статус заказов',
    description: 'Обновления по вашим заказам',
  },
  {
    key: 'promotions',
    title: 'Акции и скидки',
    description: 'Специальные предложения и скидки',
  },
  {
    key: 'newsletter',
    title: 'Новостная рассылка',
    description: 'Еженедельная рассылка с новостями отрасли',
  },
]

const nextSteps = [
  {
    title: 'Пройти обучение',
    description: 'Интерактивное руководство по основным функциям',
    action: () => startFirstTutorial(),
  },
  {
    title: 'Просмотреть каталог',
    description: 'Изучить ассортимент лабораторного оборудования',
    action: () => goToCatalog(),
  },
  {
    title: 'Найти тендеры',
    description: 'Посмотреть доступные тендеры и закупки',
    action: () => goToTenders(),
  },
]

const progress = computed(() =>
  Math.round(((currentStepIndex.value + 1) / onboardingSteps.length) * 100),
)

const canSkip = computed(() => currentStepIndex.value !== 1)

const canProceed = computed(() => {
  if (currentStepIndex.value !== 1) {
    return true
  }

  return (
    userProfile.value.companyType !== '' &&
    userProfile.value.industry !== '' &&
    userProfile.value.companySize !== ''
  )
})

const nextStep = async () => {
  if (currentStepIndex.value === onboardingSteps.length - 1) {
    await completeOnboarding()
  } else {
    currentStepIndex.value += 1
  }
}

const prevStep = () => {
  if (currentStepIndex.value > 0) {
    currentStepIndex.value -= 1
  }
}

const skipOnboarding = async () => {
  await completeOnboarding()
}

const completeOnboarding = async () => {
  try {
    localStorage.setItem('labactive-onboarding-completed', 'true')
    showOnboarding.value = false
  } catch (error) {
    console.error('Error completing onboarding:', error)
  }
}

const toggleInterest = (interest: string) => {
  if (userProfile.value.interests.includes(interest)) {
    userProfile.value.interests = userProfile.value.interests.filter((item) => item !== interest)
    return
  }

  userProfile.value.interests = [...userProfile.value.interests, interest]
}

const startFirstTutorial = () => {
  showOnboarding.value = false
  startTutorial('first-login')
}

const goToCatalog = () => {
  showOnboarding.value = false
  router.push('/catalog')
}

const goToTenders = () => {
  showOnboarding.value = false
  if (feature_flags.isEnabled('tender_enabled')) {
    router.push('/tenders')
  } else {
    router.push('/catalog')
  }
}

defineExpose({
  startOnboarding: () => {
    showOnboarding.value = true
    currentStepIndex.value = 0
  },
})
</script>

<style scoped>
.onboarding {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.onboarding__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.56);
}

.onboarding__card {
  position: relative;
  width: min(960px, calc(100vw - 24px));
  max-height: calc(100vh - 24px);
  overflow: auto;
}

.onboarding__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.onboarding__title-wrap h2 {
  margin: 0;
}

.onboarding__step-counter {
  margin-top: 4px;
  color: #586578;
  font-size: 12px;
}

.onboarding__body {
  margin-top: 12px;
}

.onboarding__step h3 {
  margin: 0 0 8px;
  font-size: 28px;
}

.onboarding__features {
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.onboarding__features article {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
}

.onboarding__icon {
  font-size: 28px;
}

.onboarding__features h4 {
  margin: 8px 0;
}

.onboarding__features p {
  margin: 0;
  color: #586578;
}

.onboarding__form-grid {
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.onboarding__form-grid > div:last-child {
  grid-column: 1 / -1;
}

.onboarding__form-grid label,
.onboarding__interest-block label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  color: #586578;
}

.onboarding__interest-block {
  margin-top: 10px;
}

.onboarding__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.onboarding__chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #334155;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
}

.onboarding__chip.is-active {
  background: #0f172a;
  color: #fff;
  border-color: #0f172a;
}

.onboarding__notification-list {
  display: grid;
  gap: 8px;
  margin-top: 12px;
}

.onboarding__notification-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
}

.onboarding__notification-title {
  font-weight: 600;
  margin-bottom: 2px;
}

.onboarding__muted {
  color: #586578;
}

.onboarding__next-steps {
  margin-top: 10px;
  display: grid;
  gap: 8px;
}

.onboarding__next-step {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  padding: 10px;
  text-align: left;
  cursor: pointer;
}

.onboarding__next-step:hover {
  background: #f8fafc;
}

.onboarding__next-step-title {
  font-weight: 600;
  margin-bottom: 4px;
}

.onboarding__actions {
  margin-top: 14px;
  display: flex;
  gap: 8px;
  align-items: center;
}

.onboarding__spacer {
  flex: 1;
}

@media (max-width: 960px) {
  .onboarding__features {
    grid-template-columns: minmax(0, 1fr);
  }

  .onboarding__form-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .onboarding__step h3 {
    font-size: 22px;
  }
}
</style>
